import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import WorkflowIndicator from '../components/WorkflowIndicator';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function SupplierManagement({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'vrfs');
  const [loading, setLoading] = useState(false);

  // Data states
  const [vrfs, setVrfs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedVrf, setSelectedVrf] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Modal states
  const [showVrfModal, setShowVrfModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showSupplierDetailModal, setShowSupplierDetailModal] = useState(false);

  // Filter states
  const [vrfFilter, setVrfFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs = [
    { id: 'vrfs', name: 'VRF Applications', icon: '📋' },
    { id: 'suppliers', name: 'All Suppliers', icon: '🏢' }
  ];

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    if (activeTab === 'vrfs') {
      await fetchVrfs();
    } else if (activeTab === 'suppliers') {
      await fetchSuppliers();
    }
  };

  const fetchVrfs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/vrfs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVrfs(response.data.vrfs || []);
    } catch (error) {
      console.error('Error fetching VRFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const openVrfDetail = (vrf) => {
    setSelectedVrf(vrf);
    setShowVrfModal(true);
  };

  const openSupplierDetail = (supplier) => {
    setSelectedSupplier(supplier);
    setShowSupplierDetailModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Under Review': 'bg-[#2b338030] text-[#2b3380]',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Blocked': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredVrfs = vrfs.filter(vrf => {
    const matchesFilter = vrfFilter === 'all' || vrf.status === vrfFilter;
    const matchesSearch = 
      vrf.entity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vrf.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vrf.vrf_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesFilter = supplierFilter === 'all' || supplier.status === supplierFilter;
    const matchesSearch = 
      supplier.entity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.supplier_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Workflow steps for Supplier Management
  const supplierWorkflowSteps = [
    { id: 'vrf', label: 'VRF Submission' },
    { id: 'kyc', label: 'KYC Verification' },
    { id: 'approval', label: 'Approval' },
    { id: 'active', label: 'Active Supplier' },
    { id: 'performance', label: 'Performance' }
  ];

  const getCurrentSupplierStep = () => {
    if (activeTab === 'vrfs') return 'vrf';
    if (activeTab === 'suppliers') return 'active';
    return 'vrf';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Supplier Management</h1>
          <p className="mt-2 text-gray-600">Manage vendor registrations and supplier information</p>
        </div>

        {/* Workflow Indicator */}
        <WorkflowIndicator 
          steps={supplierWorkflowSteps} 
          currentStep={getCurrentSupplierStep()} 
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#2b3380] text-[#2b3380]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* VRF Applications Tab */}
        {activeTab === 'vrfs' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by entity name, vendor name, or VRF ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  />
                </div>
                <select
                  value={vrfFilter}
                  onChange={(e) => setVrfFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* VRF Table */}
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VRF ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVrfs.map((vrf) => (
                      <tr key={vrf.vrf_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-mono text-gray-900">{vrf.vrf_id.slice(0, 8)}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{vrf.entity_name}</div>
                          <div className="text-xs text-gray-500">{vrf.vendor_name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{vrf.mobile_phone}</div>
                          <div className="text-xs text-gray-500">{vrf.official_email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 text-xs rounded-full font-medium ${getStatusColor(vrf.status)}`}>
                            {vrf.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {new Date(vrf.submitted_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => openVrfDetail(vrf)}
                            className="text-[#2b3380] hover:text-[#1f2660] text-sm font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredVrfs.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No VRF applications found</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Suppliers Tab */}
        {activeTab === 'suppliers' && (
          <div className="space-y-6">
            {/* Filters & Add Button */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by entity name, vendor name, or supplier ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  />
                </div>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                >
                  <option value="all">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Blocked">Blocked</option>
                </select>
                <button
                  onClick={() => setShowAddSupplierModal(true)}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] font-medium whitespace-nowrap"
                >
                  + Add Supplier
                </button>
              </div>
            </div>

            {/* Suppliers Grid */}
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuppliers.map((supplier) => (
                  <div key={supplier.supplier_id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{supplier.entity_name}</h3>
                        <p className="text-xs text-gray-500">{supplier.supplier_id}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${getStatusColor(supplier.status)}`}>
                        {supplier.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Contact:</span>
                        <span className="font-medium text-gray-900">{supplier.contact_person}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium text-gray-900">{supplier.mobile_phone}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Payment:</span>
                        <span className="font-medium text-[#2b3380]">{supplier.payment_term_type}</span>
                      </div>
                      {supplier.payment_term_type === 'Credit' && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Credit Days:</span>
                          <span className="font-medium text-gray-900">{supplier.credit_days} days</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Orders:</span>
                        <span className="font-medium text-gray-900">{supplier.total_orders}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => openSupplierDetail(supplier)}
                      className="w-full px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            )}

            {filteredSuppliers.length === 0 && !loading && (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">🏢</div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">No Suppliers Found</h3>
                <p className="text-gray-600 mb-6">Get started by adding your first supplier</p>
                <button
                  onClick={() => setShowAddSupplierModal(true)}
                  className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] font-medium"
                >
                  + Add Supplier
                </button>
              </div>
            )}
          </div>
        )}

        {/* VRF Detail Modal */}
        {showVrfModal && selectedVrf && (
          <VRFDetailModal
            vrf={selectedVrf}
            onClose={() => {
              setShowVrfModal(false);
              setSelectedVrf(null);
            }}
            onApprove={() => {
              setShowVrfModal(false);
              setShowApprovalModal(true);
            }}
            onReject={() => {
              setShowVrfModal(false);
              setShowRejectModal(true);
            }}
          />
        )}

        {/* Approval Modal */}
        {showApprovalModal && selectedVrf && (
          <ApprovalModal
            vrf={selectedVrf}
            onClose={() => {
              setShowApprovalModal(false);
              setSelectedVrf(null);
            }}
            onSuccess={() => {
              setShowApprovalModal(false);
              setSelectedVrf(null);
              fetchVrfs();
              fetchSuppliers();
            }}
          />
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedVrf && (
          <RejectModal
            vrf={selectedVrf}
            onClose={() => {
              setShowRejectModal(false);
              setSelectedVrf(null);
            }}
            onSuccess={() => {
              setShowRejectModal(false);
              setSelectedVrf(null);
              fetchVrfs();
            }}
          />
        )}

        {/* Add Supplier Modal */}
        {showAddSupplierModal && (
          <AddSupplierModal
            onClose={() => setShowAddSupplierModal(false)}
            onSuccess={() => {
              setShowAddSupplierModal(false);
              fetchSuppliers();
            }}
          />
        )}

        {/* Supplier Detail Modal */}
        {showSupplierDetailModal && selectedSupplier && (
          <SupplierDetailModal
            supplier={selectedSupplier}
            onClose={() => {
              setShowSupplierDetailModal(false);
              setSelectedSupplier(null);
            }}
            onUpdate={() => {
              fetchSuppliers();
            }}
          />
        )}
      </div>
    </Layout>
  );
}

// VRF Detail Modal Component
function VRFDetailModal({ vrf, onClose, onApprove, onReject }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">VRF Details</h2>
            <p className="text-sm text-gray-500 mt-1">ID: {vrf.vrf_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Company Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Entity Name" value={vrf.entity_name} />
              <InfoRow label="Vendor Name" value={vrf.vendor_name} />
              <InfoRow label="Address" value={vrf.complete_address} className="col-span-2" />
              <InfoRow label="GST Registered" value={vrf.gst_registered ? 'Yes' : 'No'} />
              {vrf.gst_registered && <InfoRow label="GST Number" value={vrf.gst_number} />}
              <InfoRow label="CIN" value={vrf.company_registration_no} />
              <InfoRow label="PAN" value={vrf.pan_no} />
              <InfoRow label="Nature of Business" value={vrf.nature_of_business} className="col-span-2" />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Contact Person" value={vrf.contact_person} />
              <InfoRow label="Mobile" value={vrf.mobile_phone} />
              <InfoRow label="Email" value={vrf.official_email} />
              {vrf.telephone && <InfoRow label="Telephone" value={vrf.telephone} />}
            </div>
          </div>

          {/* Bank Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Account Holder" value={vrf.account_holder_name} />
              <InfoRow label="Bank Branch" value={vrf.bank_branch_name || 'N/A'} />
              <InfoRow label="Account Number" value={vrf.account_no} />
              <InfoRow label="IFSC Code" value={vrf.ifsc_code} />
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Status" value={vrf.status} />
              <InfoRow label="Submitted At" value={new Date(vrf.submitted_at).toLocaleString()} />
              {vrf.reviewed_at && <InfoRow label="Reviewed At" value={new Date(vrf.reviewed_at).toLocaleString()} />}
              {vrf.review_remarks && <InfoRow label="Remarks" value={vrf.review_remarks} className="col-span-2" />}
            </div>
          </div>
        </div>

        {/* Actions */}
        {vrf.status === 'Pending' && (
          <div className="border-t border-gray-200 p-6 flex justify-end gap-3">
            <button
              onClick={onReject}
              className="px-6 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium"
            >
              Reject
            </button>
            <button
              onClick={onApprove}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Approve & Create Supplier
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Info Row Component
function InfoRow({ label, value, className = '' }) {
  return (
    <div className={className}>
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="mt-1 text-sm text-gray-900">{value || 'N/A'}</div>
    </div>
  );
}

// Approval Modal Component
function ApprovalModal({ vrf, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    zobox_spoc_name: '',
    zobox_spoc_email: '',
    zobox_spoc_contact: '',
    approved_by: '',
    approval_department: '',
    approval_email: '',
    approval_contact: '',
    remarks: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/vrfs/${vrf.vrf_id}/approve`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('VRF approved and supplier created successfully!');
      onSuccess();
    } catch (error) {
      alert('Error approving VRF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Approve VRF & Create Supplier</h2>
          <p className="text-sm text-gray-600 mt-1">{vrf.entity_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zobox SPOC Name *</label>
              <input
                type="text"
                value={formData.zobox_spoc_name}
                onChange={(e) => setFormData({...formData, zobox_spoc_name: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SPOC Email *</label>
              <input
                type="email"
                value={formData.zobox_spoc_email}
                onChange={(e) => setFormData({...formData, zobox_spoc_email: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SPOC Contact *</label>
              <input
                type="text"
                value={formData.zobox_spoc_contact}
                onChange={(e) => setFormData({...formData, zobox_spoc_contact: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approved By *</label>
              <input
                type="text"
                value={formData.approved_by}
                onChange={(e) => setFormData({...formData, approved_by: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
              <input
                type="text"
                value={formData.approval_department}
                onChange={(e) => setFormData({...formData, approval_department: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approval Email *</label>
              <input
                type="email"
                value={formData.approval_email}
                onChange={(e) => setFormData({...formData, approval_email: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approval Contact *</label>
              <input
                type="text"
                value={formData.approval_contact}
                onChange={(e) => setFormData({...formData, approval_contact: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (Optional)</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            >
              {loading ? 'Approving...' : 'Approve & Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reject Modal Component
function RejectModal({ vrf, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/vrfs/${vrf.vrf_id}/reject`,
        {
          rejected_by: 'Admin',
          rejection_reason: rejectionReason
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('VRF rejected successfully');
      onSuccess();
    } catch (error) {
      alert('Error rejecting VRF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Reject VRF</h2>
          <p className="text-sm text-gray-600 mt-1">{vrf.entity_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            rows="4"
            placeholder="Please provide reason for rejection..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
          />

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
            >
              {loading ? 'Rejecting...' : 'Reject VRF'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Supplier Modal Component
function AddSupplierModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    entity_name: '',
    complete_address: '',
    gst_registered: false,
    gst_number: '',
    company_registration_no: '',
    pan_no: '',
    nature_of_business: '',
    vendor_name: '',
    contact_person: '',
    telephone: '',
    mobile_phone: '',
    official_email: '',
    account_holder_name: '',
    bank_branch_name: '',
    account_no: '',
    ifsc_code: '',
    payment_term_type: 'Credit',
    credit_days: 30,
    credit_limit: 0,
    zobox_spoc_name: '',
    zobox_spoc_email: '',
    zobox_spoc_contact: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/suppliers`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('Supplier created successfully!');
      onSuccess();
    } catch (error) {
      alert('Error creating supplier: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add New Supplier</h2>
          <p className="text-sm text-gray-600 mt-1">Manually create a supplier without VRF process</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Company Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Entity Name *</label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={(e) => setFormData({...formData, entity_name: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Complete Address *</label>
                <textarea
                  value={formData.complete_address}
                  onChange={(e) => setFormData({...formData, complete_address: e.target.value})}
                  required
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.gst_registered}
                  onChange={(e) => setFormData({...formData, gst_registered: e.target.checked})}
                  className="w-4 h-4 text-[#2b3380] border-gray-300 rounded focus:ring-[#2b3380]"
                />
                <label className="ml-2 text-sm text-gray-700">GST Registered</label>
              </div>
              {formData.gst_registered && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GST Number *</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({...formData, gst_number: e.target.value})}
                    required={formData.gst_registered}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CIN *</label>
                <input
                  type="text"
                  value={formData.company_registration_no}
                  onChange={(e) => setFormData({...formData, company_registration_no: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PAN *</label>
                <input
                  type="text"
                  value={formData.pan_no}
                  onChange={(e) => setFormData({...formData, pan_no: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nature of Business *</label>
                <input
                  type="text"
                  value={formData.nature_of_business}
                  onChange={(e) => setFormData({...formData, nature_of_business: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name *</label>
                <input
                  type="text"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person *</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
                <input
                  type="tel"
                  value={formData.mobile_phone}
                  onChange={(e) => setFormData({...formData, mobile_phone: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.official_email}
                  onChange={(e) => setFormData({...formData, official_email: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder *</label>
                <input
                  type="text"
                  value={formData.account_holder_name}
                  onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account No *</label>
                <input
                  type="text"
                  value={formData.account_no}
                  onChange={(e) => setFormData({...formData, account_no: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code *</label>
                <input
                  type="text"
                  value={formData.ifsc_code}
                  onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                />
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Terms</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type *</label>
                <select
                  value={formData.payment_term_type}
                  onChange={(e) => setFormData({...formData, payment_term_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                >
                  <option value="Advance">Advance</option>
                  <option value="COD">COD</option>
                  <option value="Credit">Credit</option>
                </select>
              </div>
              {formData.payment_term_type === 'Credit' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Days *</label>
                    <input
                      type="number"
                      value={formData.credit_days}
                      onChange={(e) => setFormData({...formData, credit_days: parseInt(e.target.value)})}
                      required
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Limit</label>
                    <input
                      type="number"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value)})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* KYC Documents Upload */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📄 KYC Documents</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company GST Certificate</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CIN Document *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company PAN Card *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm font:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Director Aadhar Card *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Director PAN Card *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cancelled Cheque *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#2b3380] file:text-white hover:file:bg-[#1f2660]"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Upload PDF, JPEG, or PNG files (max 5MB each)</p>
          </div>

          <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white border-t border-gray-200 -mx-6 -mb-6 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] disabled:bg-gray-400"
            >
              {loading ? 'Creating...' : 'Create Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Supplier Detail Modal Component
function SupplierDetailModal({ supplier, onClose, onUpdate }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{supplier.entity_name}</h2>
            <p className="text-sm text-gray-500 mt-1">ID: {supplier.supplier_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 text-sm rounded-full font-medium ${
              supplier.status === 'Active' ? 'bg-green-100 text-green-800' :
              supplier.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
              'bg-red-100 text-red-800'
            }`}>
              {supplier.status}
            </span>
            {supplier.rating && (
              <div className="text-yellow-500">
                {'⭐'.repeat(Math.floor(supplier.rating))} <span className="text-gray-600">({supplier.rating})</span>
              </div>
            )}
          </div>

          {/* Company Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Vendor Name" value={supplier.vendor_name} />
              <InfoRow label="Contact Person" value={supplier.contact_person} />
              <InfoRow label="Address" value={supplier.complete_address} className="col-span-2" />
              <InfoRow label="GST Registered" value={supplier.gst_registered ? 'Yes' : 'No'} />
              {supplier.gst_registered && <InfoRow label="GST Number" value={supplier.gst_number} />}
              <InfoRow label="CIN" value={supplier.company_registration_no} />
              <InfoRow label="PAN" value={supplier.pan_no} />
              <InfoRow label="Nature of Business" value={supplier.nature_of_business} className="col-span-2" />
            </div>
          </div>

          {/* Contact & Bank */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
              <div className="space-y-3">
                <InfoRow label="Mobile" value={supplier.mobile_phone} />
                <InfoRow label="Email" value={supplier.official_email} />
                {supplier.telephone && <InfoRow label="Telephone" value={supplier.telephone} />}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
              <div className="space-y-3">
                <InfoRow label="Account Holder" value={supplier.account_holder_name} />
                <InfoRow label="Account No" value={supplier.account_no} />
                <InfoRow label="IFSC" value={supplier.ifsc_code} />
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Terms</h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoRow label="Payment Type" value={supplier.payment_term_type} />
              {supplier.payment_term_type === 'Credit' && (
                <>
                  <InfoRow label="Credit Days" value={`${supplier.credit_days} days`} />
                  <InfoRow label="Credit Limit" value={supplier.credit_limit ? `₹${supplier.credit_limit.toLocaleString()}` : 'N/A'} />
                </>
              )}
            </div>
          </div>

          {/* Purchase History */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase History</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#2b338015] rounded-lg p-4">
                <div className="text-sm text-gray-600">Total Orders</div>
                <div className="text-2xl font-bold text-gray-900">{supplier.total_orders}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Total Value</div>
                <div className="text-2xl font-bold text-gray-900">₹{(supplier.total_order_value || 0).toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Last Order</div>
                <div className="text-sm font-medium text-gray-900">
                  {supplier.last_order_date ? new Date(supplier.last_order_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* SPOC Details */}
          {supplier.zobox_spoc_name && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Zobox SPOC</h3>
              <div className="grid grid-cols-3 gap-4">
                <InfoRow label="Name" value={supplier.zobox_spoc_name} />
                <InfoRow label="Email" value={supplier.zobox_spoc_email} />
                <InfoRow label="Contact" value={supplier.zobox_spoc_contact} />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupplierManagement;
